import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import PageHeader from "../components/PageHeader";
import { Plus, Edit, Trash2, X, Copy, Users } from "lucide-react";

const HumanAgentGroups = () => {
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);

    // Group state
    const [groups, setGroups] = useState([]);
    const [humanAgents, setHumanAgents] = useState([]);
    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [groupFormData, setGroupFormData] = useState({ name: "", human_agent_ids: [] });

    // Delete confirmation state
    const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);

    const fetchGroups = async () => {
        setIsLoading(true);
        try {
            const res = await api.request("/api/human-agent-groups");
            if (res && res.groups) {
                setGroups(res.groups);
            }
        } catch (error) {
            console.error("Error fetching agent groups:", error);
            showToast("Failed to load agent groups", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchHumanAgents = async () => {
        try {
            const usersList = await api.request("/api/users");
            if (Array.isArray(usersList)) {
                // Filter active human agents
                const filtered = usersList.filter(u => (u.role === "human_agent" || u.role === "admin") && u.is_active !== false);
                setHumanAgents(filtered);
            }
        } catch (error) {
            console.error("Failed to load human agents:", error);
        }
    };

    useEffect(() => {
        fetchGroups();
        fetchHumanAgents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenGroupModal = (group = null) => {
        if (group) {
            setEditingGroup(group);
            const numIds = (group.human_agent_ids || [])
                .map(id => typeof id === "string" ? parseInt(id, 10) : id)
                .filter(id => !isNaN(id));
            setGroupFormData({ name: group.name, human_agent_ids: numIds });
        } else {
            setEditingGroup(null);
            setGroupFormData({ name: "", human_agent_ids: [] });
        }
        setIsGroupModalOpen(true);
    };

    const handleOpenDeleteGroupModal = (group) => {
        setGroupToDelete(group);
        setIsDeleteGroupModalOpen(true);
    };

    const handleGroupSubmit = async (e) => {
        e.preventDefault();

        // Local Validation: Case-insensitive duplicate check
        const normalizedName = groupFormData.name.trim().toLowerCase();
        const isDuplicate = groups.some(g =>
            g.name.trim().toLowerCase() === normalizedName &&
            (!editingGroup || g.id !== editingGroup.id)
        );

        if (isDuplicate) {
            showToast(`A group named "${groupFormData.name}" already exists.`, "error");
            return;
        }

        try {
            const payload = {
                name: groupFormData.name.trim(),
                human_agent_ids: (groupFormData.human_agent_ids || []).map(id => typeof id === "string" ? parseInt(id, 10) : id).filter(id => !isNaN(id))
            };
            if (editingGroup) {
                // Update
                await api.request(`/api/human-agent-groups/${editingGroup.id}`, {
                    method: "PUT",
                    body: JSON.stringify(payload),
                });
                showToast("Group updated successfully");
            } else {
                // Create
                await api.request("/api/human-agent-groups", {
                    method: "POST",
                    body: JSON.stringify(payload),
                });
                showToast("Group created successfully");
            }
            setIsGroupModalOpen(false);
            fetchGroups();
        } catch (error) {
            console.error("Error saving group:", error);
            const detail = error?.response?.detail || "Failed to save group";
            showToast(detail, "error");
        }
    };

    const handleDeleteGroup = async () => {
        if (!groupToDelete) return;

        setIsLoading(true);
        try {
            await api.request(`/api/human-agent-groups/${groupToDelete.id}`, {
                method: "DELETE",
            });
            showToast("Group deleted successfully");
            setIsDeleteGroupModalOpen(false);
            setGroupToDelete(null);
            fetchGroups();
        } catch (error) {
            console.error("Error deleting group:", error);
            showToast("Failed to delete group", "error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn">
            <div className="max-w-6xl mx-auto">
                <PageHeader
                    title="Human Agent Groups"
                    description="Orchestrate human operator escalation paths. Use these Group IDs in visual flows or AI instructions to route chats dynamically."
                    actions={
                        <button
                            onClick={() => handleOpenGroupModal()}
                            className="flex items-center justify-center gap-2 bg-gray-900 text-white px-8 py-2.5 rounded-xl hover:bg-black transition-all font-black text-sm uppercase shadow-xl shadow-gray-200 active:scale-95 border border-transparent"
                        >
                            <Plus size={18} />
                            Create New Group
                        </button>
                    }
                />

                <div className="bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 overflow-hidden animate-slideIn">
                    {isLoading && groups.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-32 gap-4">
                            <div className="w-10 h-10 border-4 border-gray-100 border-t-gray-900 rounded-full animate-spin"></div>
                            <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Loading Groups...</p>
                        </div>
                    ) : !groups || groups.length === 0 ? (
                        <div className="p-16 text-center">
                            <div className="w-20 h-20 bg-gray-50 rounded-[28px] border border-gray-100 flex items-center justify-center mb-6 mx-auto">
                                <Users size={32} className="text-gray-300" />
                            </div>
                            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-900 mb-2">No Groups Found</h3>
                            <p className="text-gray-400 max-w-xs mx-auto">Create your first group and assign human agents to it.</p>
                        </div>
                    ) : (
                        <div>
                            <table className="w-full text-left">
                                <thead className="bg-gray-50/50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Group Name</th>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap text-center">Group ID</th>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Assigned Human Agents</th>
                                        <th style={{ textAlign: 'center', paddingRight: '40px', width: '180px' }} className="pl-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {groups.map((group) => (
                                        <tr key={group.id} className="hover:bg-gray-50/50 transition-all group">
                                            <td className="px-6 py-6 font-bold text-gray-900 text-sm align-middle">
                                                <div className="flex items-center gap-2">
                                                    <span>{group.name}</span>
                                                    <button
                                                        onClick={() => handleOpenGroupModal(group)}
                                                        className="text-gray-400 hover:text-gray-900 hover:scale-110 transition-all p-1"
                                                        title="Edit Group Name & Assignments"
                                                    >
                                                        <Edit size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 align-middle text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <div style={{ maxWidth: '280px' }} className="max-w-[280px] overflow-x-auto whitespace-nowrap bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5 custom-scrollbar flex items-center">
                                                        <code className="text-[10px] font-mono text-gray-600 tracking-wider">
                                                            {group.group_id}
                                                        </code>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(group.group_id);
                                                            showToast("Group ID copied to clipboard");
                                                        }}
                                                        className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors flex-shrink-0 bg-gray-50 border border-gray-100 rounded-lg hover:bg-gray-100"
                                                        title="Copy Group ID"
                                                    >
                                                        <Copy size={12} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 text-sm text-gray-500 max-w-[250px] align-middle text-center">
                                                <div className="flex flex-wrap gap-1.5 justify-center">
                                                    {group.human_agent_ids && group.human_agent_ids.length > 0 ? (
                                                        group.human_agent_ids.map(id => {
                                                            const agent = humanAgents.find(a => String(a.id) === String(id));
                                                            return (
                                                                <span key={id} className="px-2.5 py-1 bg-gray-50 border border-gray-100 rounded-lg text-[10px] font-mono text-gray-600 font-bold" title={agent ? agent.username : `Agent #${id}`}>
                                                                    {agent ? (agent.human_agent_id || agent.username) : `Agent #${id}`}
                                                                </span>
                                                            );
                                                        })
                                                    ) : (
                                                        <span className="text-gray-400 italic text-xs">No Human Agents assigned</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center', paddingRight: '40px', width: '180px' }} className="pl-6 py-6 align-middle">
                                                <div className="flex justify-center gap-2">
                                                    <button
                                                        disabled={group.name === 'Default Group' || group.group_id === 'default_group'}
                                                        onClick={() => handleOpenDeleteGroupModal(group)}
                                                        className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all shadow-sm ${
                                                            (group.name === 'Default Group' || group.group_id === 'default_group')
                                                                ? "text-gray-200 bg-gray-50 border-gray-100 cursor-not-allowed"
                                                                : "text-gray-400 bg-white border border-gray-100 hover:text-red-500 hover:bg-red-50 hover:border-red-100"
                                                        }`}
                                                        title={group.name === 'Default Group' || group.group_id === 'default_group' ? "System Default Group cannot be deleted" : "Delete Group"}
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Group Modal */}
            {isGroupModalOpen && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl animate-scaleIn border border-gray-100/50">
                        <div className="px-8 py-5 border-b border-gray-50 flex justify-between items-center bg-gray-50/20">
                            <div>
                                <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase">
                                    {editingGroup ? "Edit Group" : "New Group"}
                                </h3>
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-0.5">Group Config</p>
                            </div>
                            <button
                                onClick={() => setIsGroupModalOpen(false)}
                                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-white rounded-xl transition-all"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleGroupSubmit} className="p-8 space-y-5">
                            {editingGroup && (
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Group ID</label>
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 flex items-center">
                                            <code className="text-xs font-mono text-gray-600 tracking-wider break-all select-all">
                                                {editingGroup.group_id}
                                            </code>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                navigator.clipboard.writeText(editingGroup.group_id);
                                                showToast("Group ID copied to clipboard");
                                            }}
                                            className="p-3 text-gray-400 hover:text-gray-900 transition-colors bg-gray-50 border border-gray-100 rounded-xl hover:bg-gray-100 flex-shrink-0"
                                            title="Copy Group ID"
                                        >
                                            <Copy size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Group Name</label>
                                <input
                                    type="text"
                                    required
                                    value={groupFormData.name}
                                    onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all"
                                    placeholder="e.g. Sales Team"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Assign Human Agents</label>
                                <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-xl p-3 bg-gray-50/50 space-y-2.5 custom-scrollbar">
                                    {humanAgents.map((agent) => {
                                        const humanAgentIdNum = parseInt(agent.id, 10);
                                        const isChecked = groupFormData.human_agent_ids.includes(humanAgentIdNum);
                                        return (
                                            <label key={agent.id} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-white transition-all">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => {
                                                        if (isChecked) {
                                                            setGroupFormData({
                                                                ...groupFormData,
                                                                human_agent_ids: groupFormData.human_agent_ids.filter(id => id !== humanAgentIdNum)
                                                            });
                                                        } else {
                                                            setGroupFormData({
                                                                ...groupFormData,
                                                                human_agent_ids: [...groupFormData.human_agent_ids, humanAgentIdNum]
                                                            });
                                                        }
                                                    }}
                                                    className="rounded border-gray-300 text-gray-900 focus:ring-gray-900 w-4 h-4"
                                                />
                                                <div className="flex flex-col">
                                                    <span className="text-xs font-bold text-gray-900">{agent.username}</span>
                                                    <span className="text-[10px] text-gray-400 font-mono">{agent.email}</span>
                                                </div>
                                            </label>
                                        );
                                    })}
                                    {humanAgents.length === 0 && (
                                        <p className="text-xs text-gray-400 text-center py-4">No human agents available.</p>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center justify-center gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsGroupModalOpen(false)}
                                    className="px-6 py-2.5 text-gray-500 hover:text-gray-900 font-bold text-[10px] uppercase tracking-widest transition-colors rounded-xl bg-gray-50 hover:bg-gray-100"
                                >
                                    Discard
                                </button>
                                <button
                                    type="submit"
                                    className="px-10 py-2.5 bg-gray-900 text-white font-bold text-xs uppercase tracking-widest rounded-xl hover:bg-gray-800 transition-all shadow-lg shadow-gray-200 active:scale-95"
                                >
                                    {editingGroup ? "Update Group" : "Create Group"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <DeleteConfirmModal
                isOpen={isDeleteGroupModalOpen}
                onClose={() => setIsDeleteGroupModalOpen(false)}
                onConfirm={handleDeleteGroup}
                isLoading={isLoading}
                title="Destroy Agent Group?"
                message={`This action will permanently delete group '${groupToDelete?.name}'. The agents in the group will not be affected. Are you prepared to proceed?`}
            />
        </div>
    );
};

export default HumanAgentGroups;
